/**
 * Tasks API
 */
import apiClient from './index'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Task,
  TaskCreateData,
  TaskUpdateData,
  TaskFilters,
  TaskStatus,
  Subtask,
} from '@/types'

export const tasksApi = {
  /**
   * List tasks with filtering and pagination
   */
  async list(params?: PaginationParams & TaskFilters): Promise<PaginatedResponse<Task>> {
    const response = await apiClient.get<PaginatedResponse<Task>>('/tasks', { params })
    return response.data
  },

  /**
   * Get task by ID
   */
  async get(id: string): Promise<Task> {
    const response = await apiClient.get<ApiResponse<Task>>(`/tasks/${id}`)
    return response.data.data
  },

  /**
   * Create a new task
   */
  async create(data: TaskCreateData): Promise<Task> {
    const response = await apiClient.post<ApiResponse<Task>>('/tasks', data)
    return response.data.data
  },

  /**
   * Update a task
   */
  async update(id: string, data: TaskUpdateData): Promise<Task> {
    const response = await apiClient.put<ApiResponse<Task>>(`/tasks/${id}`, data)
    return response.data.data
  },

  /**
   * Delete a task
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/tasks/${id}`)
  },

  /**
   * Update task status
   */
  async updateStatus(id: string, status: TaskStatus): Promise<Task> {
    const response = await apiClient.put<ApiResponse<Task>>(`/tasks/${id}/status`, { status })
    return response.data.data
  },

  /**
   * Get subtasks
   */
  async getSubtasks(id: string): Promise<Task[]> {
    const response = await apiClient.get<ApiResponse<Task[]>>(`/tasks/${id}/subtasks`)
    return response.data.data
  },

  /**
   * Add subtask
   */
  async addSubtask(id: string, subtask: Omit<Subtask, 'id'>): Promise<Task> {
    const response = await apiClient.post<ApiResponse<Task>>(`/tasks/${id}/subtasks`, subtask)
    return response.data.data
  },

  /**
   * Update subtask
   */
  async updateSubtask(taskId: string, subtaskId: string, data: Partial<Subtask>): Promise<Task> {
    const response = await apiClient.put<ApiResponse<Task>>(`/tasks/${taskId}/subtasks/${subtaskId}`, data)
    return response.data.data
  },

  /**
   * Delete subtask
   */
  async deleteSubtask(taskId: string, subtaskId: string): Promise<Task> {
    const response = await apiClient.delete<ApiResponse<Task>>(`/tasks/${taskId}/subtasks/${subtaskId}`)
    return response.data.data
  },

  /**
   * Get my tasks (assigned to current user)
   */
  async getMyTasks(params?: PaginationParams): Promise<PaginatedResponse<Task>> {
    const response = await apiClient.get<PaginatedResponse<Task>>('/tasks/my-tasks', { params })
    return response.data
  },
}
